// ============================================================================
// BOOTSTRAP — runtime tenant-config hydration before render (épica #55, #53)
// ============================================================================
// The platform reads its white-label configuration (theme / apps / schema) at
// RUNTIME from the public tenant-config API, cached in localStorage, so admin
// edits apply WITHOUT a redeploy. This entry:
//   1. Captures a `?companyID=` URL param (white-label by URL) into localStorage
//      so the right tenant is resolved before anything else.
//   2. Shows a neutral splash.
//   3. Hydrates the runtime config (cache-first; a short fetch on cold start).
//   4. Dynamically imports the app entry — which is when the theme/apps/schema
//      modules first read the now-populated runtime config.
//   5. Starts background revalidation (stale-while-revalidate + version reload).
//
// On any failure the app still boots from build-time JSON config (the original
// behavior), so runtime config is a progressive enhancement, never a hard dep.
// ============================================================================

// PRIMER import: guarda y quita de la URL la liga `?invite=<token>` antes de
// que arranque la analítica (utils/teamInviteBoot, WAYAK #844).
import "./utils/teamInviteBoot";
import { showSplash, hideSplash } from "./config/runtime/splash";
import { captureCompanyId } from "./config/runtime/tenantConfigClient";
import {
  ensureRuntimeConfigReady,
  startBackgroundRevalidation,
} from "./config/runtime/runtimeConfig";
import { ensureCompanyAppsOverrideReady } from "./config/runtime/companyAppsOverride";

async function bootstrap(): Promise<void> {
  // Persist (and strip) a `?companyID=` selection BEFORE resolving the tenant,
  // so a new param overrides the stored one and a root visit re-uses it.
  captureCompanyId();

  showSplash();

  // Make runtime config available before any app/config module is imported.
  await ensureRuntimeConfigReady();

  // Apply the per-company apps override (reseller company switch) before the
  // config modules read the apps preset. Theme stays the reseller's.
  await ensureCompanyAppsOverrideReady();

  // Importing the entry transitively imports ./config/theme et al, which now
  // read the hydrated runtime payload. createRoot replaces the splash.
  const { mountApp } = await import("./appEntry");
  mountApp();

  // Keep the cache warm and apply remote edits without a redeploy.
  startBackgroundRevalidation();
  // Derechos de la sesión (#649): al volver a la pestaña y cada 5 minutos.
  const { startEntitlementsRevalidation } = await import("./store/entitlementsStore");
  startEntitlementsRevalidation();
}

void bootstrap().catch((err) => {
  // A failed dynamic import (e.g. stale chunk after a deploy) would otherwise
  // strand the user on the splash forever. Remove it and surface the error.
  hideSplash();
  // eslint-disable-next-line no-console
  console.error("[bootstrap] failed to start the app", err);
});
